import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPlane, FaGraduationCap, FaUsers, FaGlobeEurope } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Hero = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    visaType: '',
    destination: ''
  });
  
  const [loading, setLoading] = useState(false);
  
  const whatsappNumber = '03135993810'; // Removed hyphen for URL encoding
  const countryCode = '92'; // Pakistan country code

  const visaTypes = [
    {
      icon: <FaPlane className="text-2xl" />,
      title: 'Tourist Visas',
      description: 'Explore the world with our hassle-free tourist visa services',
    },
    {
      icon: <FaGraduationCap className="text-2xl" />,
      title: 'Student Visas',
      description: 'Pursue your education dreams abroad with expert guidance',
    },
    {
      icon: <FaGlobeEurope className="text-2xl" />,
      title: 'Schengen Visas',
      description: 'Travel across Europe with our comprehensive Schengen visa assistance',
    },
    {
      icon: <FaUsers className="text-2xl" />,
      title: 'Family Visas',
      description: 'Reunite with loved ones through our family visa services',
    },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Format the phone number to remove any non-numeric characters
    const formattedPhone = formData.phone.replace(/\D/g, '');
    
    // Create the WhatsApp message
    const message = `*New Visa Assessment Request*%0A%0A
*Name:* ${formData.name}%0A
*Email:* ${formData.email}%0A
*Phone:* ${formData.phone}%0A
*Visa Type:* ${formData.visaType}%0A
*Destination:* ${formData.destination}%0A%0A
_I would like to get a free visa consultation._`;
    
    // Create WhatsApp URL
    const whatsappURL = `https://wa.me/${countryCode}${whatsappNumber}?text=${message}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappURL, '_blank');
    
    // Reset form after a short delay
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        visaType: '',
        destination: ''
      });
      setLoading(false);
    }, 1000);
  };

  return (
    <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 text-white overflow-hidden">
      <div className="absolute inset-0 bg-black opacity-20"></div>
      
      <div className="relative container-padding py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Your Journey Begins With The Right 
              <span className="text-primary-200"> Visa</span>
            </h1>
            <p className="text-xl text-primary-100 mb-8">
              Expert visa assistance for tourists, students, families, and Schengen travelers. 
              95% success rate with personalized service.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-primary bg-white text-primary-700 hover:bg-primary-100 text-center">
                Start Your Application
              </Link>
              <Link to="/process" className="btn-primary border-white text-white hover:bg-white/10 text-center">
                Learn More
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
              {visaTypes.map((type, index) => (
                <motion.div
                  key={type.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition"
                >
                  <div className="text-primary-200 mb-2">{type.icon}</div>
                  <h3 className="font-semibold mb-1">{type.title}</h3>
                  <p className="text-sm text-primary-100">{type.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-8 text-gray-800">
              <h3 className="text-2xl font-bold mb-6 text-primary-900">
                Free Visa Assessment
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="+92 313 5993810"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Visa Type
                  </label>
                  <select 
                    name="visaType"
                    value={formData.visaType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select Visa Type</option>
                    <option value="Tourist Visa">Tourist Visa</option>
                    <option value="Student Visa">Student Visa</option>
                    <option value="Schengen Visa">Schengen Visa</option>
                    <option value="Family Visa">Family Visa</option>
                    <option value="Business Visa">Business Visa</option>
                    <option value="Work Visa">Work Visa</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Destination Country
                  </label>
                  <input
                    type="text"
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="United States, UK, Canada, etc."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full btn-primary mt-6 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Redirecting...
                    </span>
                  ) : (
                    'Get Free Consultation'
                  )}
                </button>
                
                <p className="text-sm text-gray-600 text-center mt-4">
                  By submitting, you agree to our terms and privacy policy.
                  <br />
                  <span className="text-primary-600 font-medium">
                    You'll be redirected to WhatsApp to send your details
                  </span>
                </p>
              </form>
            </div>
            
            <div className="absolute -z-10 -top-4 -right-4 w-64 h-64 bg-primary-400 rounded-full blur-3xl opacity-30"></div>
            <div className="absolute -z-10 -bottom-4 -left-4 w-64 h-64 bg-primary-300 rounded-full blur-3xl opacity-30"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;